import { action, makeObservable, observable } from 'mobx';
import { IAnyModelType, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { v4 as uuidv4 } from 'uuid';
import { LayerType } from '../utils/layerTypeEnums';

export interface IUpdateLayerBaseProps {
  title: string | undefined;
  opacity?: number;
  saturate?: number;
  invert?: number;
  bloom?: number;
  brightness?: number;
  contrast?: number;
  sepia?: number;
  visible?: boolean;
}

export interface IUpdateLayerProps extends IUpdateLayerBaseProps {
  opacityDarkAppearance?: number;
  saturateDarkAppearance?: number;
  invertDarkAppearance?: number;
  bloomDarkAppearance?: number;
  brightnessDarkAppearance?: number;
  contrastDarkAppearance?: number;
  sepiaDarkAppearance?: number;
}

export interface ILayerProps extends IUpdateLayerProps {
  identifier: string;
  id: number | string | undefined;
  name: string | undefined; // works as identifier for WMS sublayers instead of id
  sublayers?: ILayerProps[];
  isBaseMap: boolean;
  wkid?: number;
  tileMatrixSetId?: string; // used for WMTS services to set spatial reference
  layerType?: LayerType;
  serviceType?: string;
  url?: string;
  portalId?: string;
  portalUrl?: string;
  serverUrl?: string;
  loaded?: boolean;
  role?: string;
  version?: string;
  username?: string;
  password?: string;
  token?: string;
  serviceUrl?: string;
}

export interface ILayer extends ILayerProps {
  sublayers: ILayer[] | undefined;
  addSublayer: (layer: ILayerProps) => void;
  updateLayer: (values: Partial<IUpdateLayerProps>) => void;
}

export class LayerMobxModel implements ILayerProps {
  identifier: string = '';
  id: number | string | undefined;
  name: string | undefined; // works as identifier for WMS sublayers instead of id
  title: string | undefined;
  sublayers: LayerMobxModel[] = [];
  isBaseMap: boolean = false;
  wkid?: number;
  tileMatrixSetId?: string; // used for WMTS services to set spatial reference
  layerType?: LayerType;
  serviceType?: string;
  url?: string;
  portalId?: string;
  portalUrl?: string;
  serverUrl?: string;
  opacity?: number;
  visible?: boolean;
  loaded?: boolean;
  role?: string;
  version?: string;
  username?: string;
  password?: string;
  token?: string;
  serviceUrl?: string;

  constructor(options?: ILayerProps) {
    if (options) {
      const { sublayers, ...rest } = options;
      Object.assign(this, rest);
      sublayers && sublayers.forEach((subLayer) => this.addSublayer(subLayer));
    }
    makeObservable(this, {
      identifier: observable,
      id: observable,
      name: observable,
      title: observable,
      sublayers: observable,
      isBaseMap: observable,
      wkid: observable,
      tileMatrixSetId: observable,
      layerType: observable,
      serviceType: observable,
      url: observable,
      portalId: observable,
      portalUrl: observable,
      serverUrl: observable,
      opacity: observable,
      visible: observable,
      loaded: observable,
      role: observable,
      version: observable,
      username: observable,
      password: observable,
      token: observable,
      serviceUrl: observable,
      addSublayer: action,
      updateLayer: action
    });
  }

  public addSublayer(layer: ILayerProps) {
    if (!layer?.identifier) {
      layer.identifier = uuidv4();
    }
    if (this.sublayers) {
      this.sublayers.push(new LayerMobxModel(layer));
    } else {
      this.sublayers = [new LayerMobxModel(layer)];
    }
  }
  public updateLayer(values: Partial<IUpdateLayerProps>) {
    Object.assign(this, values);
  }
}

export const LayerModel = types
  .model('LayerModel', {
    identifier: types.identifier,
    id: types.maybe(types.union(types.number, types.string)),
    name: types.maybe(types.string), // works as identifier for WMS sublayers instead of id
    title: types.maybe(types.string),
    sublayers: types.array(types.late((): IAnyModelType => LayerModel)),
    isBaseMap: types.optional(types.boolean, false),
    wkid: types.maybe(types.number),
    tileMatrixSetId: types.maybe(types.string), // used for WMTS services to set spatial reference
    layerType: types.maybe(types.enumeration('LayerType', Object.values(LayerType))),
    serviceType: types.maybe(types.string),
    url: types.maybe(types.string),
    portalId: types.maybe(types.string),
    portalUrl: types.maybe(types.string),
    serverUrl: types.maybe(types.string),
    opacity: types.maybe(types.number),
    opacityDarkAppearance: types.maybe(types.number),
    saturate: types.maybe(types.number),
    saturateDarkAppearance: types.maybe(types.number),
    invert: types.maybe(types.number),
    invertDarkAppearance: types.maybe(types.number),
    bloom: types.maybe(types.number),
    bloomDarkAppearance: types.maybe(types.number),
    brightness: types.maybe(types.number),
    brightnessDarkAppearance: types.maybe(types.number),
    contrast: types.maybe(types.number),
    contrastDarkAppearance: types.maybe(types.number),
    sepia: types.maybe(types.number),
    sepiaDarkAppearance: types.maybe(types.number),
    visible: types.maybe(types.boolean),
    loaded: types.maybe(types.boolean),
    role: types.maybe(types.string),
    version: types.maybe(types.string),
    username: types.maybe(types.string),
    password: types.maybe(types.string),
    token: types.maybe(types.string),
    serviceUrl: types.maybe(types.string)
  })
  .actions((self) => ({
    addSublayer(layer: ILayerProps) {
      layer.identifier = uuidv4();
      if (self.sublayers) {
        self.sublayers.push(layer);
      } else {
        (self as ILayerModelInstance).sublayers.replace([layer]);
      }
    },
    updateLayer(values: Partial<IUpdateLayerProps>) {
      Object.assign(self, values);
    }
  }));

export interface ILayerModelSnapshotIn extends SnapshotIn<typeof LayerModel> {}
export interface ILayerModelInstance extends Instance<typeof LayerModel> {}
